import React, { useEffect, useState } from 'react';
import { organizationService, locationService, businessCategoryService } from '../services';
import { Organization, CreateOrganizationRequest, BusinessCategory } from '../types';
import { City } from '../services/locations';
import { Pagination } from '../components/Pagination';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Building2,
  Edit,
  Eye,
  Globe,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Plus,
  Trash2,
} from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

interface OrganizationFormData {
  // Organization fields
  name: string;
  description?: string;
  contactEmail: string;
  contactPhone: string;
  website?: string;
  address?: string;
  cityId: string;
  active?: boolean;

  // Main branch fields
  branchName: string;
  branchDescription?: string;
  branchBusinessCategoryIds: number[];
  branchAddress: string;
  branchCityId: string;
  branchCommissionRate?: number | '';
  branchBaseCommission?: number | '';
  branchMinPeople?: number | '';
  branchMaxPeople?: number | '';
}

export const OrganizationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [businessCategories, setBusinessCategories] = useState<BusinessCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [selectedCityId, setSelectedCityId] = useState<string>('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm<OrganizationFormData>();

  useEffect(() => {
    loadOrganizations(currentPage, pageSize);
    loadCities();
    loadBusinessCategories();
  }, [currentPage, pageSize]);

  const loadOrganizations = async (page: number = 0, size: number = 10) => {
    try {
      setLoading(true);
      setError(null);
      const response = await organizationService.getAllOrganizations(page, size);
      setOrganizations(response.content);
      setTotalElements(response.totalElements);
      setTotalPages(response.totalPages);
    } catch (err: any) {
      setError(err.message || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const loadCities = async () => {
    try {
      const citiesData = await locationService.getAllCities();
      setCities(citiesData);
    } catch (err: any) {
      console.error('Failed to load cities:', err);
    }
  };

  const loadBusinessCategories = async () => {
    try {
      const categoriesData = await businessCategoryService.getAllCategoriesAdmin(false);
      setBusinessCategories(categoriesData);
    } catch (err: any) {
      console.error('Failed to load business categories:', err);
    }
  };

  const handleOpenDialog = async (org?: Organization) => {
    if (org) {
      setEditingOrg(org);
      // Load the full organization details to get the city
      try {
        const fullOrg = await organizationService.getOrganizationById(String(org.id));
        // Find the city ID from the city name if available
        const city = cities.find((c) => c.name === fullOrg.city);
        setSelectedCityId(city ? String(city.id) : '');

        reset({
          name: fullOrg.name,
          description: fullOrg.description || '',
          contactEmail: fullOrg.contactEmail || '',
          contactPhone: fullOrg.contactPhone || '',
          website: fullOrg.website || '',
          address: fullOrg.address || '',
        });
      } catch (err: any) {
        console.error('Failed to load organization details:', err);
        reset({
          name: org.name,
          description: org.description || '',
          contactEmail: org.contactEmail || '',
          contactPhone: org.contactPhone || '',
          website: org.website || '',
          address: org.address || '',
        });
      }
    } else {
      setEditingOrg(null);
      setSelectedCategories([]);
      setSelectedCityId('');
      reset({
        name: '',
        description: '',
        contactEmail: '',
        contactPhone: '',
        website: '',
        address: '',
        cityId: '',
        active: true,
        branchName: '',
        branchDescription: '',
        branchBusinessCategoryIds: [],
        branchAddress: '',
        branchCityId: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingOrg(null);
    setSelectedCategories([]);
    setSelectedCityId('');
    reset({});
  };

  const toggleCategory = (categoryId: number) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]
    );
  };

  const onSubmit = async (data: OrganizationFormData) => {
    try {
      if (editingOrg) {
        // Update organization (only org fields, not main branch)
        await organizationService.updateOrganization(String(editingOrg.id), {
          name: data.name,
          description: data.description,
          contactEmail: data.contactEmail,
          contactPhone: data.contactPhone,
          website: data.website,
          address: data.address,
          city: selectedCityId ? parseInt(selectedCityId) : undefined,
        });
      } else {
        // Create organization with main branch
        if (selectedCategories.length === 0) {
          setError('Please select at least one business category for the main branch');
          return;
        }

        const createRequest: CreateOrganizationRequest = {
          name: data.name,
          description: data.description,
          contactEmail: data.contactEmail,
          contactPhone: data.contactPhone,
          website: data.website,
          address: data.address,
          cityId: parseInt(data.cityId),
          active: data.active ?? true,
          mainBranch: {
            name: data.branchName,
            description: data.branchDescription,
            businessCategoryIds: selectedCategories,
            address: data.branchAddress,
            cityId: parseInt(data.branchCityId),
            commissionRate:
              data.branchCommissionRate !== undefined &&
              data.branchCommissionRate !== null &&
              data.branchCommissionRate !== ''
                ? Number(data.branchCommissionRate)
                : null,
            baseCommission:
              data.branchBaseCommission !== undefined &&
              data.branchBaseCommission !== null &&
              data.branchBaseCommission !== ''
                ? Number(data.branchBaseCommission)
                : null,
            minPeople:
              data.branchMinPeople !== undefined &&
              data.branchMinPeople !== null &&
              data.branchMinPeople !== ''
                ? Number(data.branchMinPeople)
                : null,
            maxPeople:
              data.branchMaxPeople !== undefined &&
              data.branchMaxPeople !== null &&
              data.branchMaxPeople !== ''
                ? Number(data.branchMaxPeople)
                : null,
          },
        };

        await organizationService.createOrganization(createRequest);
      }
      await loadOrganizations(currentPage, pageSize);
      handleCloseDialog();
    } catch (err: any) {
      setError(err.message || 'Failed to save organization');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this organization?')) {
      try {
        await organizationService.deleteOrganization(id);
        await loadOrganizations(currentPage, pageSize);
      } catch (err: any) {
        setError(err.message || 'Failed to delete organization');
      }
    }
  };

  if (loading && organizations.length === 0) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Organizations</h1>
          <p className="text-muted-foreground">Manage organizations and their details</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Add Organization
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <p className="font-medium">Error</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>All Organizations</CardTitle>
          <CardDescription>List of all organizations in the system</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Branches</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {organizations.map((org) => (
                <TableRow key={org.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{org.name}</p>
                        {org.description && (
                          <p className="text-sm text-muted-foreground">{org.description}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1 text-sm">
                      {org.contactEmail && (
                        <div className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {org.contactEmail}
                        </div>
                      )}
                      {org.contactPhone && (
                        <div className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {org.contactPhone}
                        </div>
                      )}
                      {org.website && (
                        <div className="flex items-center gap-1">
                          <Globe className="h-3 w-3" />
                          <a
                            href={org.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            {org.website}
                          </a>
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {org.city || org.country ? (
                      <div className="flex items-center gap-1 text-sm">
                        <MapPin className="h-3 w-3" />
                        {[org.city, org.country].filter(Boolean).join(', ')}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{org.branchCount}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/organizations/${org.id}`)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(org)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(String(org.id))}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {organizations.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-8">No organizations found</p>
          )}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalElements={totalElements}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            itemName="organizations"
          />
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingOrg ? 'Edit Organization' : 'Create Organization'}</DialogTitle>
            <DialogDescription>
              {editingOrg
                ? 'Update organization details'
                : 'Add a new organization with a main branch to the system'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Organization Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Organization Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="name">Organization Name *</Label>
                  <Input id="name" {...register('name', { required: true })} />
                  {errors.name && <p className="text-sm text-destructive">Name is required</p>}
                </div>

                <div className="col-span-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea id="description" {...register('description')} />
                </div>

                <div>
                  <Label htmlFor="contactEmail">Contact Email *</Label>
                  <Input
                    id="contactEmail"
                    type="email"
                    {...register('contactEmail', { required: !editingOrg })}
                  />
                  {errors.contactEmail && (
                    <p className="text-sm text-destructive">Email is required</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="contactPhone">Contact Phone *</Label>
                  <Input
                    id="contactPhone"
                    {...register('contactPhone', { required: !editingOrg })}
                  />
                  {errors.contactPhone && (
                    <p className="text-sm text-destructive">Phone is required</p>
                  )}
                </div>

                <div className="col-span-2">
                  <Label htmlFor="website">Website</Label>
                  <Input id="website" {...register('website')} placeholder="https://" />
                </div>

                <div className="col-span-2">
                  <Label htmlFor="address">Address</Label>
                  <Input id="address" {...register('address')} />
                </div>

                {!editingOrg && (
                  <div className="col-span-2">
                    <Label htmlFor="cityId">City *</Label>
                    <Controller
                      name="cityId"
                      control={control}
                      rules={{ required: !editingOrg }}
                      render={({ field }) => (
                        <Select onValueChange={field.onChange} value={field.value}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a city" />
                          </SelectTrigger>
                          <SelectContent>
                            {cities.map((city) => (
                              <SelectItem key={city.id} value={String(city.id)}>
                                {city.name} {city.countryName ? `(${city.countryName})` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.cityId && <p className="text-sm text-destructive">City is required</p>}
                  </div>
                )}

                {editingOrg && (
                  <div className="col-span-2">
                    <Label htmlFor="editCityId">City</Label>
                    <Select value={selectedCityId} onValueChange={setSelectedCityId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a city (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {cities.map((city) => (
                          <SelectItem key={city.id} value={String(city.id)}>
                            {city.name} {city.countryName ? `(${city.countryName})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            {/* Main Branch Section - Only for new organizations */}
            {!editingOrg && (
              <div className="space-y-4 border-t pt-4">
                <h3 className="text-lg font-semibold">Main Branch Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="branchName">Branch Name *</Label>
                    <Input id="branchName" {...register('branchName', { required: !editingOrg })} />
                    {errors.branchName && (
                      <p className="text-sm text-destructive">Branch name is required</p>
                    )}
                  </div>

                  <div className="col-span-2">
                    <Label htmlFor="branchDescription">Branch Description</Label>
                    <Textarea id="branchDescription" {...register('branchDescription')} />
                  </div>

                  <div className="col-span-2">
                    <Label>Business Categories *</Label>
                    <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-2">
                      {businessCategories.map((category) => (
                        <label
                          key={category.id}
                          className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={selectedCategories.includes(category.id)}
                            onChange={() => toggleCategory(category.id)}
                            className="rounded"
                          />
                          <span className="text-sm">{category.name}</span>
                        </label>
                      ))}
                    </div>
                    {selectedCategories.length === 0 && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Select at least one category
                      </p>
                    )}
                  </div>

                  <div className="col-span-2">
                    <Label htmlFor="branchAddress">Branch Address *</Label>
                    <Input
                      id="branchAddress"
                      {...register('branchAddress', { required: !editingOrg })}
                    />
                    {errors.branchAddress && (
                      <p className="text-sm text-destructive">Branch address is required</p>
                    )}
                  </div>

                  <div className="col-span-2">
                    <Label htmlFor="branchCityId">Branch City *</Label>
                    <Controller
                      name="branchCityId"
                      control={control}
                      rules={{ required: !editingOrg }}
                      render={({ field }) => (
                        <Select onValueChange={field.onChange} value={field.value}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a city" />
                          </SelectTrigger>
                          <SelectContent>
                            {cities.map((city) => (
                              <SelectItem key={city.id} value={String(city.id)}>
                                {city.name} {city.countryName ? `(${city.countryName})` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.branchCityId && (
                      <p className="text-sm text-destructive">Branch city is required</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="branchCommissionRate">Commission Rate (%)</Label>
                    <Input
                      id="branchCommissionRate"
                      type="number"
                      step="0.01"
                      placeholder="Optional"
                      {...register('branchCommissionRate')}
                    />
                  </div>

                  <div>
                    <Label htmlFor="branchBaseCommission">Base Commission</Label>
                    <Input
                      id="branchBaseCommission"
                      type="number"
                      step="0.01"
                      placeholder="Optional"
                      {...register('branchBaseCommission')}
                    />
                  </div>

                  <div>
                    <Label htmlFor="branchMinPeople">Min People</Label>
                    <Input
                      id="branchMinPeople"
                      type="number"
                      placeholder="Optional"
                      {...register('branchMinPeople')}
                    />
                  </div>

                  <div>
                    <Label htmlFor="branchMaxPeople">Max People</Label>
                    <Input
                      id="branchMaxPeople"
                      type="number"
                      placeholder="Optional"
                      {...register('branchMaxPeople')}
                    />
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit">{editingOrg ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrganizationsPage;
